// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import loadCarouselProducts from "@insite/client-framework/Store/Components/ProductCarousel/Handlers/LoadCarouselProducts";
import { getProductsForProductInfoList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import translate from "@insite/client-framework/Translate";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FancyScroll from "@pacific/App/Components/Common/FancyScroll";
import TitleTypography, { TitleTypographyStyles } from "@pacific/App/Components/Common/TitleTypography";
import { CustomAppState } from "@pacific/App/Store/Reducers";
import { xIsAuthenticated } from "@pacific/App/Store/Selectors/GeneralSelectors";
import { getCustomTypeSession } from "@pacific/App/Store/Selectors/SessionSelectors";
import { MappedLink } from "@pacific/MobiusOverrides/Menu/Menu";
import ProductCarouselProduct, {
    ProductCarouselProductStyles,
} from "@pacific/Overrides/Components/ProductCarouselProduct";
import breakpoints from "@pacific/XDL/PreStyle/breakpoints";
import { color, ellipsis, styledScrollbar } from "@pacific/XDL/Utilities";
import { getDataLayer } from "@pacific/XDL/Utilities/dataLayer";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface CategoryMenuItemProps {
    links: MappedLink[];
    title: string;
    categoryUrl?: string;
    menuWidth: number;
    categoryId?: string;
}

const getCarouselId = (categoryId?: string) => {
    return categoryId ? `topsellers_${categoryId}` : "";
};

const mapStateToProps = (state: CustomAppState, ownProps: CategoryMenuItemProps) => {
    const session = getCustomTypeSession(state);
    const currentBillTo = getCurrentBillToState(state).value;
    if (!ownProps.categoryId) {
        return {
            products: undefined,
            carouselId: "",
            isLoading: false,
            isAuthenticated: xIsAuthenticated(state),
            session,
            currentBillTo,
        };
    }
    const carouselId = getCarouselId(ownProps.categoryId);
    return {
        products: getProductsForProductInfoList(state, carouselId),
        carouselId,
        isLoading: state.xProductList.isProductCarouselLoading,
        isAuthenticated: xIsAuthenticated(state),
        session,
        currentBillTo,
    };
};

const mapDispatchToProps = {
    loadCarouselProducts,
};

type Props = CategoryMenuItemProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CategoryMenuItemStyle {
    wrapper?: InjectableCss;
    titleTypographyStyles?: TitleTypographyStyles;
    linksWrapper?: InjectableCss;
    link?: LinkPresentationProps;
    carouselTitle?: TypographyProps;
    carouselWrapper?: InjectableCss;
    carouselCenteringWrapper?: InjectableCss;
    productCarouselProductStyles?: ProductCarouselProductStyles;
    scrollWrapper?: InjectableCss;
}

const styles: CategoryMenuItemStyle = {
    wrapper: {
        css: css`
            width: ${({ width }: any) => width}px;
            max-width: calc(100vw - 366px);
            height: 504px;
            padding: 20px 16px;
            max-height: calc(100vh - 150px);
            background: ${color("common.ui1")};
            ${styledScrollbar(2)}
            overflow-y: overlay;

            & .links-section {
                padding-bottom: 12px;
                max-height: 170px;
                min-height: 170px;
                ${styledScrollbar(4)}
            }
        `,
    },
    scrollWrapper: {
        css: css`
            & > div {
                border-bottom: 1px solid ${color("common.ui4")};
                margin-bottom: 12px;
            }
        `,
    },
    titleTypographyStyles: {
        wrapper: {
            css: css`
                margin-bottom: 12px;
            `,
        },
        title: {
            variant: "h6",
        },
        link: {
            icon: {
                iconProps: {
                    size: 5,
                },
            },
            typographyProps: {
                size: 10,
                weight: 600,
                css: css`
                    letter-spacing: 0;
                    line-height: 13px;
                `,
            },
        },
    },
    linksWrapper: {
        css: css`
            display: flex;
            align-items: center;
            flex-wrap: wrap;
        `,
    },
    link: {
        color: "text.main",
        typographyProps: {
            size: 12,
            weight: 500,
            css: css`
                letter-spacing: 0;
                line-height: 18px;
                max-width: calc(100% - 16px);
                ${ellipsis(1)}
            `,
        },
        css: css`
            flex-basis: calc(100% / 4);
            margin-bottom: 16px;
        `,
    },
    carouselTitle: {
        variant: "h6",
        color: "text",
        as: "p",
        css: css`
            margin-bottom: 12px;
        `,
    },
    carouselWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            gap: 32px 50px;
            & div[data-test-selector="productContainer"] {
                height: unset;
                max-width: ${({ width }: any) => `calc(calc(${width}px / 3) - 50px)`};
                ${({ isAuthenticated }: any) => {
                    if (isAuthenticated) {
                        return css`
                            .product-grid-container-priceAndActions {
                                max-height: 52px;
                            }
                        `;
                    }
                }}
            }
        `,
    },
    carouselCenteringWrapper: {
        css: css`
            display: flex;
            min-height: 200px;
            align-items: center;
            justify-content: center;
        `,
    },
    productCarouselProductStyles: {
        productPartNumbersStyles: {
            container: {
                css: css`
                    margin-bottom: 0;
                `,
            },
        },
        productPriceAndActionsStyle: {
            productActionsStyles: {
                addToCartButtonWrapper: {
                    css: css`
                        display: flex;
                        margin-bottom: 0;
                        height: 28px;
                    `,
                },
            },
            productGridContainer: {
                addToCartActions: {
                    css: css`
                        margin-bottom: 0;
                    `,
                },
            },
        },
        productImageStyles: {
            img: {
                urlOptions: "t_prdsm",
            } as any,
        },
        productAvailabilityStyles: {
            inventoryMessage: {
                css: css`
                    margin-bottom: 0;
                `,
            },
        },
    },
};

const CategoryMenuItem: FC<Props> = ({
    links,
    title,
    categoryUrl,
    menuWidth,
    categoryId,
    products,
    carouselId,
    loadCarouselProducts,
    isLoading,
    isAuthenticated,
    session,
    currentBillTo,
}) => {
    const wrapperEl = React.createRef<HTMLDivElement>();
    const { maxWidths, paddingValues } = breakpoints as any;
    const pageInnerWidth = maxWidths[maxWidths.length - 1] - paddingValues[paddingValues.length - 1] * 2;
    const featuredProductsNumber = links && links.length > 0 ? 3 : 6;
    useEffect(() => {
        if (!categoryId || !carouselId) {
            return;
        }

        loadCarouselProducts({
            carouselId,
            carouselType: "topSellers",
            displayProductsFrom: "selectedCategories",
            selectedCategoryIds: [categoryId],
            numberOfProductsToDisplay: featuredProductsNumber,
            isProductDetailsPage: false,
            isProductListPage: false,
            isBrandDetailsPage: false,
            relatedProductType: "",
            seedWithManuallyAssigned: "",
        });
    }, [categoryId]);
    const shouldShowFeaturedProducts = carouselId && products && products.length && !isLoading;

    const _GaNavBarEvent = (link: MappedLink) => {
        const dataLayer = getDataLayer();
        if (!dataLayer) {
            return;
        }

        const googleAnalyticsData = {
            event: "nav_bar",
            click_text: link.title,
            destination: link.url,
            authentication_state: isAuthenticated ? "Authenticated" : "Guest",
            affiliation: currentBillTo?.companyName || "",
            sales_rep: (session as any)?.salesperson?.name || "",
        };
        dataLayer.push(googleAnalyticsData);
    };

    return (
        <StyledWrapper {...styles.wrapper} className="category-menu-item" {...{ width: pageInnerWidth - menuWidth }}>
            {links && links.length > 0 && (
                <>
                    <TitleTypography
                        linkText={translate("View All")}
                        link={categoryUrl}
                        onLinkClick={() => _GaNavBarEvent({ title: "View All", url: categoryUrl })}
                        extendedStyles={styles.titleTypographyStyles}
                    >
                        {title}
                    </TitleTypography>
                    <StyledWrapper {...styles.scrollWrapper}>
                        <FancyScroll refElement={wrapperEl}>
                            <StyledWrapper ref={wrapperEl} className="links-section">
                                <StyledWrapper {...styles.linksWrapper}>
                                    {links.map(link => (
                                        <Link
                                            key={link.url}
                                            href={link.url}
                                            {...styles.link}
                                            aria-label={link.title}
                                            onClick={() => _GaNavBarEvent(link)}
                                        >
                                            {link.title}
                                        </Link>
                                    ))}
                                </StyledWrapper>
                            </StyledWrapper>
                        </FancyScroll>
                    </StyledWrapper>
                </>
            )}

            <StyledWrapper className="featured-products-section" key={carouselId}>
                {shouldShowFeaturedProducts && (
                    <>
                        <Typography {...styles.carouselTitle}>{translate("Featured Products")}</Typography>
                        <StyledWrapper
                            {...styles.carouselWrapper}
                            {...{ width: pageInnerWidth - menuWidth }}
                            {...{ isAuthenticated }}
                        >
                            {products?.slice(0, featuredProductsNumber).map(product => (
                                <ProductCarouselProduct
                                    key={product.id}
                                    carouselId={carouselId}
                                    product={product}
                                    showImage={true}
                                    showBrand={true}
                                    showTitle={true}
                                    showPartNumbers={true}
                                    showPrice={true}
                                    showAddToCart={true}
                                    showAddToCartIcon={true}
                                    showAddToList={true}
                                    showImageBadges={true}
                                    maximumNumberOfImageBadges={5}
                                    showTextBadges={true}
                                    cardView="medium"
                                    menuItemView={true}
                                    extendedStyles={styles.productCarouselProductStyles}
                                />
                            ))}
                        </StyledWrapper>
                    </>
                )}
                {isLoading && (
                    <StyledWrapper {...styles.carouselCenteringWrapper}>
                        <LoadingSpinner />
                    </StyledWrapper>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(CategoryMenuItem);
